import React, { useEffect, useMemo, useRef, useState } from 'react';

import { RiAlertLine, RiUpload2Line } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Input, InputRoot } from '@/design-system/input';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import {
  ImportCommitResult,
  ImportDuplicateMode,
  ImportPreviewResult,
  InvoiceClientOption,
  InvoiceImportError,
  invoicesAPI,
} from '@/services/api/invoices';

type Stage = 'pick' | 'preview' | 'committing';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (result: ImportCommitResult, clientName: string) => void;
}

const friendlyImportError = (err: unknown): string => {
  if (err instanceof InvoiceImportError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
};

const csvEscape = (value: string): string => {
  if (/[,"\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const buildErrorCsv = (preview: ImportPreviewResult): Blob => {
  const header = ['row_number', 'field', 'value', 'message'];
  const lines = [header.join(',')];
  for (const e of preview.errors) {
    lines.push(
      [
        String(e.rowNumber),
        csvEscape(e.field),
        csvEscape(e.value ?? ''),
        csvEscape(e.message),
      ].join(',')
    );
  }
  return new Blob(['﻿' + lines.join('\r\n') + '\r\n'], {
    type: 'text/csv;charset=utf-8',
  });
};

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const [stage, setStage] = useState<Stage>('pick');
  const [clientId, setClientId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [activeTab, setActiveTab] = useState<
    'toCreate' | 'duplicates' | 'errors'
  >('toCreate');
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<ImportDuplicateMode | null>(
    null
  );

  const [clientOptions, setClientOptions] = useState<InvoiceClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStage('pick');
    setClientId('');
    setDueDate('');
    setFile(null);
    setSubmitting(false);
    setInlineError(null);
    setPreview(null);
    setActiveTab('toCreate');
    setDuplicateConfirmOpen(false);
    setPendingMode(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setClientsLoading(true);
    setClientsError(null);
    invoicesAPI
      .clients()
      .then((items) => {
        if (!cancelled) setClientOptions(items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setClientsError(friendlyImportError(err));
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Escape + body-scroll-lock are handled by <Modal>. We just need to
  // keep the duplicate-confirm dialog dismissable on Escape while it's open.
  useEffect(() => {
    if (!isOpen || !duplicateConfirmOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        setDuplicateConfirmOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, duplicateConfirmOpen, submitting]);

  const clientName = useMemo(
    () => clientOptions.find((c) => c.id === clientId)?.name ?? '',
    [clientOptions, clientId]
  );

  if (!isOpen) return null;

  const handleFile = (f: File | null) => {
    setFile(f);
    setInlineError(null);
  };

  const handlePreview = async () => {
    if (!clientId || !file) return;
    setInlineError(null);
    setSubmitting(true);
    try {
      const result = await invoicesAPI.importDryRun({
        clientId,
        dueDate: dueDate || null,
        file,
      });
      setPreview(result);
      setStage('preview');
      const next: 'toCreate' | 'duplicates' | 'errors' =
        result.summary.errors > 0
          ? 'errors'
          : result.summary.duplicates > 0
            ? 'duplicates'
            : 'toCreate';
      setActiveTab(next);
    } catch (err) {
      const msg = friendlyImportError(err);
      if (
        err instanceof InvoiceImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(msg);
      } else {
        setInlineError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.errors > 0) return;
    if (preview.summary.duplicates > 0) {
      setDuplicateConfirmOpen(true);
      setPendingMode('skip');
      return;
    }
    void doCommit('skip');
  };

  const doCommit = async (mode: ImportDuplicateMode) => {
    if (!file || !clientId || !preview) return;
    setDuplicateConfirmOpen(false);
    setStage('committing');
    setSubmitting(true);
    setInlineError(null);
    try {
      const result = await invoicesAPI.importCommit({
        clientId,
        dueDate: dueDate || null,
        duplicateMode: mode,
        file,
      });
      onImported(result, clientName);
      const total = result.created + result.updated;
      const parts: string[] = [];
      if (result.created > 0) parts.push(`${result.created} created`);
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      toast.success(
        `Imported ${total} invoice${total === 1 ? '' : 's'}${
          clientName ? ` for ${clientName}` : ''
        }`,
        { description: parts.join(' · ') || 'No changes made.' }
      );
      onClose();
    } catch (err) {
      if (
        err instanceof InvoiceImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(
          err.message ||
            'The file no longer matches the database state. Re-preview before committing.'
        );
      } else {
        setStage('preview');
        setInlineError(friendlyImportError(err));
        toast.error('Import failed', { description: friendlyImportError(err) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadErrors = () => {
    if (!preview) return;
    const blob = buildErrorCsv(preview);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoice-import-errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const stopDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const canPreview =
    Boolean(clientId) && Boolean(file) && !submitting && stage === 'pick';

  const blockCommit =
    !preview ||
    preview.summary.errors > 0 ||
    preview.summary.toCreate + preview.summary.duplicates === 0;

  const pickFooter = (
    <div className="flex items-center justify-end gap-2">
      <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handlePreview} disabled={!canPreview}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Previewing…
          </>
        ) : (
          'Preview import'
        )}
      </Button>
    </div>
  );

  const previewFooter = preview && (
    <div className="flex items-center justify-end gap-2">
      <Button
        tone="neutral" style="stroke"
        onClick={() => {
          setStage('pick');
          setPreview(null);
          setInlineError(null);
        }}
        disabled={submitting}
      >
        Back
      </Button>
      <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handleConfirm} disabled={blockCommit || submitting}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Importing…
          </>
        ) : (
          `Confirm import${
            preview.summary.toCreate + preview.summary.duplicates > 0
              ? ` (${
                  preview.summary.toCreate + preview.summary.duplicates
                })`
              : ''
          }`
        )}
      </Button>
    </div>
  );

  return (
    <>
      <FormModal
        open={isOpen}
        onClose={onClose}
        icon={<RiUpload2Line />}
        iconTone="primary"
        title="Import invoices from CSV"
        subtitle="Backfill historical invoices for one client at a time. Up to 5,000 rows per file."
        size="xl"
        padded={false}
        closeOnBackdropClick={!submitting}
        closeOnEscape={!submitting && !duplicateConfirmOpen}
        footer={stage === 'pick' ? pickFooter : previewFooter}
      >
        {stage === 'pick' && (
          <div className="space-y-5 p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="import-client"
                  className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
                >
                  Client <span className="text-error-base">*</span>
                </label>
                <SearchSelect
                  options={clientOptions.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  value={clientId || undefined}
                  onChange={(v) => setClientId((v as string | undefined) ?? '')}
                  placeholder={
                    clientsLoading ? 'Loading clients…' : 'Pick a client'
                  }
                  disabled={submitting || clientsLoading}
                  loading={clientsLoading}
                  emptyMessage="No clients found"
                />
                {clientsError && (
                  <p className="mt-1 text-[11px] text-error-base">
                    {clientsError}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="import-duedate"
                  className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
                >
                  Due date (optional)
                </label>
                <InputRoot size="md">
                  <Input
                    id="import-duedate"
                    type="date"
                    value={dueDate}
                    disabled={submitting}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </InputRoot>
                <p className="mt-1 text-[11px] text-text-sub-600">
                  Applied to every row in the batch.
                </p>
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  CSV file <span className="text-error-base">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => invoicesAPI.downloadTemplate()}
                  className="text-xs font-semibold tracking-tight text-accent hover:underline"
                  aria-label="Download CSV template"
                >
                  Download template
                </button>
              </div>
              <div
                onDrop={onDrop}
                onDragOver={stopDrag}
                onDragEnter={stopDrag}
                className="rounded-lg border-2 border-dashed border-stroke-soft-200 bg-bg-weak-50 px-4 py-8 text-center hover:border-accent transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,application/vnd.ms-excel"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-sm text-text-sub-600">
                    <i className="ri-file-text-line text-[20px] text-accent" />
                    <div className="text-left">
                      <p className="font-medium text-text-strong-950">{file.name}</p>
                      <p className="text-xs text-text-sub-600">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFile(null)}
                      className="ml-2 text-xs text-text-sub-600 hover:text-error-base"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm text-text-sub-600"
                  >
                    <span className="block">
                      Drag &amp; drop your CSV here, or
                      <span className="ml-1 font-semibold text-accent">
                        browse
                      </span>
                    </span>
                    <span className="mt-1 block text-xs text-text-sub-600">
                      .csv only · max 5 MB
                    </span>
                  </button>
                )}
              </div>
              <p className="mt-2 text-[11px] text-text-sub-600">
                Required: invoice_number, airwaybill, items_count, issue_date.
                Dates as YYYY-MM-DD.
              </p>
            </div>

            {inlineError && (
              <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}

        {(stage === 'preview' || stage === 'committing') && preview && (
          <div className="space-y-4 p-5">
            <PreviewSummary
              preview={preview}
              clientName={clientName}
            />

            {preview.summary.ignoredColumns.length > 0 && (
              <div className="rounded-lg border border-warning-light bg-warning-lighter px-3 py-2 text-xs text-warning-dark">
                Ignored unknown columns:{' '}
                <span className="font-mono">
                  {preview.summary.ignoredColumns.join(', ')}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
              <PreviewTab
                active={activeTab === 'toCreate'}
                onClick={() => setActiveTab('toCreate')}
                label="To create"
                count={preview.summary.toCreate}
                tone="emerald"
              />
              <PreviewTab
                active={activeTab === 'duplicates'}
                onClick={() => setActiveTab('duplicates')}
                label="Duplicates"
                count={preview.summary.duplicates}
                tone="amber"
              />
              <PreviewTab
                active={activeTab === 'errors'}
                onClick={() => setActiveTab('errors')}
                label="Errors"
                count={preview.summary.errors}
                tone="red"
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-stroke-soft-200">
              {activeTab === 'toCreate' && (
                <ToCreateTable preview={preview} />
              )}
              {activeTab === 'duplicates' && (
                <DuplicatesTable preview={preview} />
              )}
              {activeTab === 'errors' && (
                <ErrorsTable
                  preview={preview}
                  onDownload={handleDownloadErrors}
                />
              )}
            </div>

            {inlineError && (
              <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}
      </FormModal>

      {/* Nested duplicate-confirm dialog. Rendered as a sibling Modal so it
          stacks on top of the import modal naturally (latest in DOM wins). */}
      {preview && (
        <FormModal
          open={duplicateConfirmOpen}
          onClose={() => !submitting && setDuplicateConfirmOpen(false)}
          icon={<RiAlertLine />}
          iconTone="warning"
          title={`${preview.summary.duplicates} invoice number${
            preview.summary.duplicates === 1 ? '' : 's'
          } already exist${preview.summary.duplicates === 1 ? 's' : ''}.`}
          subtitle="Choose how to handle them. The choice applies to the whole batch."
          size="sm"
          closeOnBackdropClick={!submitting}
          closeOnEscape={!submitting}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                tone="neutral" style="stroke"
                onClick={() => setDuplicateConfirmOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                tone="destructive"
                onClick={() => {
                  setPendingMode('overwrite');
                  void doCommit('overwrite');
                }}
                disabled={submitting}
              >
                Overwrite
              </Button>
              <Button
                onClick={() => {
                  setPendingMode('skip');
                  void doCommit('skip');
                }}
                disabled={submitting}
              >
                Skip
              </Button>
            </div>
          }
        >
          <ul className="space-y-2 text-xs text-text-sub-600">
            <li>
              <strong>Skip duplicates</strong> — the existing invoices stay
              untouched. Only the {preview.summary.toCreate} new row
              {preview.summary.toCreate === 1 ? '' : 's'} are created.
            </li>
            <li>
              <strong>Overwrite duplicates</strong> — the existing invoices
              are updated with the values from this CSV. Status and
              timestamps are preserved.{' '}
              <span className="text-error-base">This cannot be undone.</span>
            </li>
          </ul>
        </FormModal>
      )}
      {/* keep pendingMode referenced for future telemetry */}
      <span hidden>{pendingMode ?? ''}</span>
    </>
  );
};

interface PreviewSummaryProps {
  preview: ImportPreviewResult;
  clientName: string;
}

const PreviewSummary: React.FC<PreviewSummaryProps> = ({
  preview,
  clientName,
}) => {
  const cards: { label: string; value: number; tone: string }[] = [
    {
      label: 'To create',
      value: preview.summary.toCreate,
      tone: 'text-success-base bg-success-lighter border-success-light',
    },
    {
      label: 'Duplicates',
      value: preview.summary.duplicates,
      tone: 'text-warning-dark bg-warning-lighter border-warning-light',
    },
    {
      label: 'Errors',
      value: preview.summary.errors,
      tone: 'text-error-base bg-error-lighter border-error-light',
    },
  ];
  return (
    <div>
      <p className="mb-2 text-xs text-text-sub-600">
        {preview.summary.totalRows} row
        {preview.summary.totalRows === 1 ? '' : 's'} parsed
        {clientName ? ` · ${clientName}` : ''}
      </p>
      <div className="grid grid-cols-3 gap-3">
        {cards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg border ${card.tone} px-4 py-3`}
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]">
              {card.label}
            </p>
            <p className="mt-2 text-2xl font-bold tracking-tight tabular-nums">
              {card.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PreviewTabProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone: 'emerald' | 'amber' | 'red';
}

const PreviewTab: React.FC<PreviewTabProps> = ({
  active,
  onClick,
  label,
  count,
  tone,
}) => {
  const toneActive =
    tone === 'emerald'
      ? 'bg-success-lighter text-success-base'
      : tone === 'amber'
        ? 'bg-warning-lighter text-warning-dark'
        : 'bg-error-lighter text-error-base';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold tracking-tight transition-colors ${
        active
          ? 'bg-bg-white-0 text-text-strong-950 border border-stroke-soft-200'
          : 'text-text-sub-600 hover:text-text-strong-950 border border-transparent'
      }`}
    >
      {label}
      <span
        className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
          active ? toneActive : 'bg-bg-soft-200 text-text-sub-600'
        }`}
      >
        {count}
      </span>
    </button>
  );
};

const ToCreateTable: React.FC<{ preview: ImportPreviewResult }> = ({
  preview,
}) => {
  if (preview.summary.toCreate === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No new rows to create.
      </div>
    );
  }
  return (
    <div className="max-h-[280px] overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          <tr>
            <th className="px-3 py-2">Row</th>
            <th className="px-3 py-2">Invoice #</th>
            <th className="px-3 py-2">AWB</th>
            <th className="px-3 py-2">Issue date</th>
            <th className="px-3 py-2 text-right">Items</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.toCreate.map((r) => (
            <tr key={r.rowNumber} className="border-t border-stroke-soft-200">
              <td className="px-3 py-1.5 tabular-nums text-text-sub-600">
                {r.rowNumber}
              </td>
              <td className="px-3 py-1.5 font-mono">{r.invoiceNumber}</td>
              <td className="px-3 py-1.5 font-mono">{r.airwaybill}</td>
              <td className="px-3 py-1.5 tabular-nums">{r.issueDate}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {r.itemsCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {preview.summary.toCreate > preview.toCreate.length && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[11px] text-text-sub-600">
          Showing first {preview.toCreate.length} of {preview.summary.toCreate}.
        </div>
      )}
    </div>
  );
};

const DuplicatesTable: React.FC<{ preview: ImportPreviewResult }> = ({
  preview,
}) => {
  if (preview.summary.duplicates === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No duplicates found.
      </div>
    );
  }
  return (
    <div className="max-h-[280px] overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          <tr>
            <th className="px-3 py-2">Row</th>
            <th className="px-3 py-2">Invoice #</th>
            <th className="px-3 py-2">CSV</th>
            <th className="px-3 py-2">Existing in DB</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.duplicates.map((d) => (
            <tr key={d.rowNumber} className="border-t border-stroke-soft-200 align-top">
              <td className="px-3 py-2 tabular-nums text-text-sub-600">
                {d.rowNumber}
              </td>
              <td className="px-3 py-2 font-mono">{d.invoiceNumber}</td>
              <td className="px-3 py-2">
                <p className="font-mono text-[11px]">{d.csv.airwaybill}</p>
                <p className="text-[11px] text-text-sub-600 tabular-nums">
                  {d.csv.issueDate} · {d.csv.itemsCount} item
                  {d.csv.itemsCount === 1 ? '' : 's'}
                </p>
              </td>
              <td className="px-3 py-2">
                <p className="font-mono text-[11px]">
                  {d.existing.airwaybill}
                </p>
                <p className="text-[11px] text-text-sub-600 tabular-nums">
                  {d.existing.issueDate} · {d.existing.itemsCount} item
                  {d.existing.itemsCount === 1 ? '' : 's'} ·{' '}
                  <span className="font-medium text-text-sub-600">
                    {d.existing.status}
                  </span>
                </p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {preview.summary.duplicates > preview.duplicates.length && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[11px] text-text-sub-600">
          Showing first {preview.duplicates.length} of{' '}
          {preview.summary.duplicates}.
        </div>
      )}
    </div>
  );
};

const ErrorsTable: React.FC<{
  preview: ImportPreviewResult;
  onDownload: () => void;
}> = ({ preview, onDownload }) => {
  if (preview.summary.errors === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No errors. You're good to go.
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <p className="text-[11px] text-text-sub-600">
          {preview.summary.errors} row error
          {preview.summary.errors === 1 ? '' : 's'} blocking commit.
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-accent hover:underline"
        >
          <i className="ri-download-2-line text-[14px]" />
          Download error CSV
        </button>
      </div>
      <div className="max-h-[260px] overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="text-text-strong-950">
            {preview.errors.map((e, idx) => (
              <tr
                key={`${e.rowNumber}-${e.field}-${idx}`}
                className="border-t border-stroke-soft-200"
              >
                <td className="px-3 py-1.5 tabular-nums text-text-sub-600">
                  {e.rowNumber}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{e.field}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-text-sub-600">
                  {e.value || <span className="italic text-text-soft-400">empty</span>}
                </td>
                <td className="px-3 py-1.5 text-error-base">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportModal;
